import styled from '@emotion/styled';
import {FormControlLabel} from '@mui/material';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import React, {ChangeEvent} from 'react';

import {FormInputWrapper} from '@ELib/components/form/FormInputWrapper';
import {SharedProps} from '@ELib/components/form/sharedProps';
import {mixinRoundFocus} from '@ELib/styles/mixins';

export type ERadioItem<T = string> = {
  value: T;
  label: string;
};

export enum ERadioGroupAlignment {
  Row,
  Column,
}

export type ERadioGroupProps = SharedProps & {
  label?: string;
  items: ERadioItem[];
  alignment?: ERadioGroupAlignment;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  value: string;
};

export const ERadioGroup = ({
  name,
  label,
  value,
  items,
  error,
  errorMessage,
  labelInfo,
  bottomText,
  alignment = ERadioGroupAlignment.Column,
  onChange,
}: ERadioGroupProps): React.ReactElement => (
  <FormInputWrapper
    bottomText={bottomText}
    className='ERadioGroup'
    error={error}
    errorMessage={errorMessage}
    label={label}
    labelInfo={labelInfo}
    labelProps={{htmlFor: name}}
  >
    <RadioGroup
      data-testid={`ERadioGroup-${name}`}
      row={alignment === ERadioGroupAlignment.Row}
      value={value}
      onChange={onChange}
    >
      {items.map((item: ERadioItem) => (
        <StyledFormControl
          key={item.value}
          control={
            <StyledRadioButton
              data-testid={`RadioButton-${item.value}`}
              disableRipple
            />
          }
          label={item.label}
          value={item.value}
        />
      ))}
    </RadioGroup>
  </FormInputWrapper>
);

export const StyledFormControl = styled(FormControlLabel)`
  width: fit-content;
  & .MuiFormControlLabel-label {
    font-weight: var(--font-weight-thin);
    color: var(--color-core-black);
  }

  & .MuiRadio-root {
    padding-top: var(--gutter-tiny);
    padding-bottom: var(--gutter-tiny);
  }

  && .Mui-checked {
    color: var(--radio-checked-color);
  }
`;

export const StyledRadioButton = styled(Radio)`
  :focus-within {
    svg {
      ${mixinRoundFocus}
    }
  }
`;
